import axios from "axios";
import { GOOGLE_SPREADSHEET_URL, PRODUCTS_SHEET_URL } from "../config/spreadsheet";
import {
  buildCustomisationImageUrls,
  buildProductMediaFromRow,
  firstImageUrlsForShop,
} from "./parseProductMedia";
import { buildBoxSizeLabel } from "./formatBoxDimensions";

/** Handles BOM, trailing spaces, or odd casing on Google Sheet column headers. */
export function normalizeSheetRowKeys(row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    if (k == null) continue;
    const key = String(k)
      .replace(/^\uFEFF/, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    out[key] = v;
  }
  return out;
}

function parseNumericCell(row, rawRow, ...headerNames) {
  for (const name of headerNames) {
    const nk = String(name).toLowerCase().replace(/\s+/g, " ").trim();
    const v = row[nk];
    if (v != null && v !== "") {
      const n = Number(String(v).replace(/,/g, "").trim());
      if (Number.isFinite(n)) return n;
    }
  }
  for (const name of headerNames) {
    const k = String(name).trim();
    if (rawRow[k] != null && rawRow[k] !== "") {
      const n = Number(String(rawRow[k]).replace(/,/g, "").trim());
      if (Number.isFinite(n)) return n;
    }
  }
  return NaN;
}

function readCell(row, rawRow, ...headerNames) {
  for (const name of headerNames) {
    const nk = String(name).toLowerCase().replace(/\s+/g, " ").trim();
    const v = row[nk];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  for (const name of headerNames) {
    const k = String(name).trim();
    if (rawRow[k] != null && String(rawRow[k]).trim() !== "") {
      return String(rawRow[k]).trim();
    }
  }
  return "";
}

/**
 * Products pricing is driven by **evaluated values** from the sheet (opensheet returns numbers, not formulas).
 * Keep business rules like `=CEILING((H2+N2)*1.8,50)-1` and any discount math **in Google Sheets** so one source
 * of truth can be edited without redeploying the site.
 *
 * Current box catalog columns (see src/config/spreadsheet.js PRODUCTS_HEADER_ROW):
 *
 * Fallback when “New selling price” is empty: if P > 0 and Selling price > 0, customer price = Selling price × (1 − P/100).
 * Otherwise customer price = Selling price (or 0 if missing).
 * “Customisation image” — dedicated Drive URL for /customise lid preview (optional).
 * “Customisation photo #” — pick photo 1, 2, 3… from Photo link(s) when no dedicated URL (optional).
 * If both empty, /customise falls back to the first product photo.
 */
function opensheetPermissionMessage() {
  return (
    "Product catalog is not public yet. In Google Sheets open " +
    GOOGLE_SPREADSHEET_URL +
    " → Share → Anyone with the link → Viewer. Then refresh this page."
  );
}

export async function fetchProductsFromSheet() {
  let res;
  try {
    res = await axios.get(PRODUCTS_SHEET_URL);
  } catch (err) {
    const msg = err?.response?.data?.error;
    if (msg === "The caller does not have permission") {
      throw new Error(opensheetPermissionMessage());
    }
    throw err;
  }

  if (res.data && res.data.error === "The caller does not have permission") {
    throw new Error(opensheetPermissionMessage());
  }
  if (!Array.isArray(res.data)) {
    throw new Error("Product catalog returned an unexpected response from the sheet.");
  }

  const formatted = res.data
    .filter((p) => {
      const row = normalizeSheetRowKeys(p);
      return readCell(row, p, "Box id", "Box ID", "Saree ID", "Product ID", "ID");
    })
    .map((p, index) => {
      const row = normalizeSheetRowKeys(p);
      const legacyPhoto = (
        p["Photo link(s)"] ||
        p["Photo links"] ||
        p["Photo link"] ||
        ""
      ).trim();
      const legacyVideo = (
        p["Video links"] ||
        p["Video link(s)"] ||
        p["Video link"] ||
        ""
      ).trim();
      const legacyCustomisationImage = (
        p["Customisation image"] ||
        p["Customization image"] ||
        p["Customisation screen image"] ||
        ""
      ).trim();
      const legacyCustomisationPhotoIndex = (
        p["Customisation photo #"] ||
        p["Customization photo #"] ||
        p["Customise image #"] ||
        ""
      ).trim();
      const { imageGroups, galleryItems, videos } = buildProductMediaFromRow(
        row,
        legacyPhoto,
        legacyVideo,
      );
      const customisationImages = buildCustomisationImageUrls(
        row,
        legacyCustomisationImage,
        imageGroups,
        legacyCustomisationPhotoIndex,
      );
      const firstImageGroup = firstImageUrlsForShop(imageGroups, galleryItems);
      const boxId = readCell(row, p, "Box id", "Box ID", "Saree ID", "Product ID", "ID") || String(index + 1);
      const category = readCell(row, p, "Category", "Type") || "Gift Box";
      const supplierCode = readCell(row, p, "Saree supplier code", "Supplier code", "Supplier");
      const height = parseNumericCell(row, p, "Height");
      const length = parseNumericCell(row, p, "Length");
      const width = parseNumericCell(row, p, "Width");
      const weight = parseNumericCell(row, p, "Weight");
      const cost = parseNumericCell(row, p, "Cost");
      const totalCost = parseNumericCell(row, p, "Total cost", "Total Cost");
      const boxSizeLabel = buildBoxSizeLabel({ height, length, width });

      const listPriceRaw = parseNumericCell(row, p, "Selling price");
      const listPrice = Number.isFinite(listPriceRaw) ? Math.max(0, listPriceRaw) : 0;
      const newSellingRaw = parseNumericCell(
        row,
        p,
        "New selling price",
        "Effective selling price",
        "Offer price",
        "Discounted selling price",
        "Final selling price",
      );
      const discountPctRaw = parseNumericCell(
        row,
        p,
        "Discount percent",
        "Discount %",
        "Discount percentage",
      );
      const discountPercent = Number.isFinite(discountPctRaw)
        ? Math.max(0, Math.min(100, discountPctRaw))
        : 0;

      let price = listPrice;
      if (Number.isFinite(newSellingRaw) && newSellingRaw > 0) {
        price = newSellingRaw;
      } else if (discountPercent > 0 && listPrice > 0) {
        price = Math.round(listPrice * (100 - discountPercent)) / 100;
      }

      const hasDiscount =
        (listPrice > 0 && price > 0 && price + 0.001 < listPrice) ||
        (discountPercent > 0 && price > 0);

      return {
        id: boxId,
        name: readCell(row, p, "Name"),
        boxId,
        /** Backward compatibility with existing cart/order/review code paths. */
        saree_id: boxId,
        /** List / compare-at price (Selling price, column O). */
        listPrice,
        /** Discount % from column P (badge when > 0). */
        discountPercent,
        /** True when effective price is below list (new price or % discount). */
        hasDiscount,
        price,
        color: readCell(row, p, "Color"),
        boxGroup: readCell(row, p, "Box Group", "Box group", "Group"),
        height: Number.isFinite(height) ? height : "",
        length: Number.isFinite(length) ? length : "",
        width: Number.isFinite(width) ? width : "",
        weight: Number.isFinite(weight) ? weight : "",
        cost: Number.isFinite(cost) ? cost : "",
        totalCost: Number.isFinite(totalCost) ? totalCost : "",
        boxSizeLabel,
        dimensions:
          Number.isFinite(length) && Number.isFinite(width) && Number.isFinite(height)
            ? { length, width, height }
            : null,
        /** Existing UI uses `fabric` as the secondary filter label; use box size for new schema. */
        fabric: boxSizeLabel,
        category,
        supplierCode,
        description: readCell(row, p, "Description"),
        status: readCell(row, p, "Status"),
        stock: Number(readCell(row, p, "Quantity")) || 0,
        /** First still-image only (shop cards); embed slides excluded */
        images: firstImageGroup,
        /** Lid/customise preview only — from “Customisation image” column; not used on shop/product */
        customisationImages,
        /** Image-only groups (Drive video links are not here — use galleryItems) */
        imageGroups: imageGroups.length
          ? imageGroups
          : firstImageGroup.length
            ? [firstImageGroup]
            : [],
        /** Full gallery order: images + mixed Drive/video embeds from Photo link(s) */
        galleryItems: galleryItems.length ? galleryItems : [],
        videos,
      };
    });
  return formatted;
}
